import type { OrganizationLong } from 'src/api/main'

import { paths } from './paths'

/**
 * Where a freshly-landed user should go, decided purely from their workspaces
 * and their *remembered* (intentionally chosen) workspace.
 *
 * - `create` — no workspaces yet → the create flow.
 * - `enter`  — a remembered-and-still-valid workspace, or the only workspace.
 * - `select` — more than one workspace and no remembered choice → let them pick.
 *
 * `storedWorkspaceId` must be the *intentional* preference (the workspace the
 * user actually used last), never the in-memory fallback — see
 * `UserSessionContextProvider`. Validation against the live workspace list lives
 * here, so a workspace the user lost access to falls through to `select`.
 */
export type WorkspaceLanding =
  | { kind: 'create' }
  | { kind: 'enter'; workspaceId: number }
  | { kind: 'select' }

export const resolveWorkspaceLanding = (
  workspaces: OrganizationLong[],
  storedWorkspaceId: number | undefined
): WorkspaceLanding => {
  if (workspaces.length === 0) {
    return { kind: 'create' }
  }

  const remembered = workspaces.find(
    (workspace) => workspace.id === storedWorkspaceId
  )
  if (remembered) {
    return { kind: 'enter', workspaceId: remembered.id }
  }

  if (workspaces.length === 1) {
    return { kind: 'enter', workspaceId: workspaces[0].id }
  }

  return { kind: 'select' }
}

/** Map a landing decision to the route to navigate to. */
export const workspaceLandingPath = (landing: WorkspaceLanding): string => {
  switch (landing.kind) {
    case 'create':
      return paths.workspaces.new
    case 'enter':
      return paths.workspaces.home(String(landing.workspaceId))
    case 'select':
      return paths.workspaces.select
  }
}
